import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ProfileField, ProfileDataType } from '../../types/profile';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import useDeleteParams from '../../hooks/useDeleteParams';
import { PersonalLinkData } from '../../types/invites';
import { connectionService, linkService, personalLinkService, profileService } from '../../services';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';
import { SuccessPageState } from '../../types/navigation';
import useProfileFields from '../../hooks/useProfileFields';
import { getUserId } from '../../services/httpService';

export default function AuthenticatedPersonalLinkPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();

  useDeleteParams('link');

  const linkId = params.get('linkId');

  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [newFields, setNewFields] = useState<Partial<Record<ProfileDataType, ProfileField>>>({});
  const [linkData, setLinkData] = useState<PersonalLinkData>();
  const [noDataSharedModalOpen, setNoDataSharedModalOpen] = useState<boolean>(false);

  useEffect(() => {
    linkService
      .getLinkInfo(linkId as string)
      .then((linkInfo) => setLinkData(linkInfo.data as PersonalLinkData))
      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });
  }, []);

  useEffect(() => {
    if (!linkData || profileFields.length === 0) return;

    const loggedInUserId = getUserId();
    if (+linkData.userId === +loggedInUserId) {
      //this is the loggedin user own invite
      toast.warning('Je kan niet reageren op je eigen link');
      navigate('/');
    }

    connectionService
      .getSharedDataWithUser(linkData.userId.toString())
      .then((data) => {
        setSelectedData(data);
      })
      .catch((): void => {});
  }, [linkData, profileFields]);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setNoDataSharedModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async () => {
    // Save new fields first if any exist
    let allSelectedData = [...selectedData];
    const newFieldsToSave = Object.values(newFields).filter((field) => field && !field.id);

    if (newFieldsToSave.length > 0) {
      try {
        const savedFields = await profileService.addFields(newFieldsToSave);
        allSelectedData = [...selectedData, ...savedFields];
      } catch (error) {
        toast.error(t('toast.error.general.saveFailed'));
        return;
      }
    }

    const sharedProps = allSelectedData.map((d) => +(d.id || ''));
    personalLinkService.usePersonalLink(linkId as string, sharedProps).then(() => {
      const state: SuccessPageState = {
        translationKey: 'page.common.success.message.personalLink',
        translationOptions: {
          name: linkData?.firstName!,
        },
      };
      navigate('/success', { state });
    });
  };

  return (
    <>
      <ResponsePageWrapper>
        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col">
            <h2 className="text-lg font-medium">{t('page.shared.requestedData')}</h2>
            <p className="text-sm">
              {t('page.invite.message', {
                name: linkData?.firstName || '',
                dataRequest: '',
              })}{' '}
              {t('page.invite.selectData')}
            </p>
          </div>

          <UserDataForm
            selectedData={selectedData || []}
            setSelectedData={setSelectedData}
            newFields={newFields}
            onNewFieldsChange={setNewFields}
            hasSelectAll
          />
          <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <NoDataSharedModal
        open={noDataSharedModalOpen}
        setOpen={setNoDataSharedModalOpen}
        requesterName={linkData?.firstName || ''}
        handleComplete={handleSubmit}
      />
    </>
  );
}
